import { getClient } from '@matix/api-client';
import type { ShipmentEvent, ShipmentStatus, ShipmentView, ShippingProviderCode, ShippingProviderOption } from '@matix/types';

/**
 * Shipping API client for the till.
 *
 * Mirrors pos-api/src/pos/shipping/shipping.routes.ts. Uses the shared axios
 * instance — same JWT, same 401 redirect, same loader — because this is one
 * more surface of the cashier app. Every call is branch-scoped by the server.
 */

const BASE = '/api/v1/shipping';

function http() {
  return getClient().axios;
}

export interface QuoteResponse {
  cost: number;
  currency: string;
  breakdown: Array<{ label: string; amount: number }>;
  customer_charge: number;
  shipment: ShipmentView;
}

export interface LabelResponse {
  url: string | null;
  base64: string | null;
  content_type: string | null;
  tracking_url: string | null;
}

export const shippingApi = {
  async providers(): Promise<ShippingProviderOption[]> {
    const { data } = await http().get<{ data: ShippingProviderOption[] }>(`${BASE}/providers`);
    return data.data;
  },
  async forReceipt(receiptId: number): Promise<{ current: ShipmentView | null; history: ShipmentView[] }> {
    const { data } = await http().get(`${BASE}/shipments`, { params: { receipt_id: receiptId } });
    return data;
  },
  async forOrders(receiptIds: number[]): Promise<Record<number, ShipmentView>> {
    if (receiptIds.length === 0) return {};
    const { data } = await http().get<{ data: Record<number, ShipmentView> }>(`${BASE}/shipments/for-orders`, {
      params: { receipt_ids: receiptIds.join(',') },
    });
    return data.data;
  },
  async assign(receiptId: number, provider: ShippingProviderCode, notes?: string | null): Promise<ShipmentView> {
    const { data } = await http().post<{ shipment: ShipmentView }>(`${BASE}/shipments`, { receipt_id: receiptId, provider_code: provider, notes: notes ?? null });
    return data.shipment;
  },
  async changeProvider(id: number, provider: ShippingProviderCode): Promise<ShipmentView> {
    const { data } = await http().patch<{ shipment: ShipmentView }>(`${BASE}/shipments/${id}/provider`, { provider_code: provider });
    return data.shipment;
  },
  async quote(id: number): Promise<QuoteResponse> {
    const { data } = await http().post<QuoteResponse>(`${BASE}/shipments/${id}/quote`);
    return data;
  },
  async create(id: number, body: { cod?: boolean | null; notes?: string | null; scheduled_at?: string | null }): Promise<ShipmentView> {
    const { data } = await http().post<{ shipment: ShipmentView }>(`${BASE}/shipments/${id}/create`, body);
    return data.shipment;
  },
  async refresh(id: number, force = true): Promise<ShipmentView> {
    const { data } = await http().post<{ shipment: ShipmentView }>(`${BASE}/shipments/${id}/refresh`, undefined, { params: { force } });
    return data.shipment;
  },
  async cancel(id: number, reason?: string | null): Promise<ShipmentView> {
    const { data } = await http().post<{ shipment: ShipmentView }>(`${BASE}/shipments/${id}/cancel`, { reason: reason ?? null });
    return data.shipment;
  },
  async label(id: number): Promise<LabelResponse> {
    const { data } = await http().get<LabelResponse>(`${BASE}/shipments/${id}/label`);
    return data;
  },
  async events(id: number): Promise<ShipmentEvent[]> {
    const { data } = await http().get<{ data: ShipmentEvent[] }>(`${BASE}/shipments/${id}/events`);
    return data.data;
  },
  async manual(id: number, body: { tracking_number?: string | null; tracking_url?: string | null; actual_cost?: number | null; driver_name?: string | null; driver_phone?: string | null }): Promise<ShipmentView> {
    const { data } = await http().patch<{ shipment: ShipmentView }>(`${BASE}/shipments/${id}/manual`, body);
    return data.shipment;
  },
  async setStatus(id: number, status: ShipmentStatus, note?: string | null): Promise<ShipmentView> {
    const { data } = await http().post<{ shipment: ShipmentView }>(`${BASE}/shipments/${id}/status`, { status, note: note ?? null });
    return data.shipment;
  },
};
